import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

import { lastValueFrom } from 'rxjs';

import { BaasAppState } from '@lvflanders/bar';
import { BaasClickOutsideBehavior } from '@lvflanders/bar-ng-ui';
import { StartschermMailOnsDialog, StartschermV2StateService } from '@lvflanders/startscherm-ng-ui';

import { TevredenheidSubmitDto } from '../../api/models';
import { TevredenheidsApiService } from '../../api/services';
import { CreateTevredenheidDto } from './../../api/models/create-tevredenheid-dto';

const PLACEHOLDER = 'Geef eerst een of meerdere redenen waarom u ontevreden bent.';

type TevredenheidFormModel = {
    reason: FormControl<string>;
    comment: FormControl<string>;
};

@Component({
    selector: 'startscherm-tevredenheidsmeter',
    templateUrl: './startscherm-tevredenheidsmeter.html',
    styleUrls: ['./startscherm-tevredenheidsmeter.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BaasClickOutsideBehavior,
        MatIconButton,
        MatIcon,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        MatInput,
        MatButton
    ]
})
export class StartschermTevredenheidsmeter implements OnInit {
    readonly #appState = inject(BaasAppState);
    readonly #staTevredenheidService = inject(TevredenheidsApiService);
    readonly #startschermStateService = inject(StartschermV2StateService);
    readonly #fb = inject(FormBuilder);
    readonly #dialog = inject(MatDialog);
    readonly #destroyRef = inject(DestroyRef);

    readonly data = input<{ dossiernummer?: string; dossierVersie?: string }>(undefined);

    protected readonly tevredenheidsForm: FormGroup<TevredenheidFormModel> = this.#fb.group({
        reason: [''],
        comment: [{ value: PLACEHOLDER, disabled: true }]
    });

    protected readonly lvCategories = toSignal(this.#staTevredenheidService.getTevredenheidsCategorien({ toepassingsCode: 'STARTSCHERM' }));
    protected readonly showWaarom = signal<boolean>(false);
    protected readonly init = signal(false);
    protected readonly activeEmotion = signal<number>(undefined);
    protected readonly minimized = signal<boolean>(false);
    protected readonly emotions = signal([
        { name: 'Tevreden', color: 'success', value: 1, class: 'fa-smile' },
        { name: 'Neutraal', color: 'warning', value: 2, class: 'fa-meh' },
        { name: 'Ontevreden', color: 'error', value: 3, class: 'fa-frown' }
    ]).asReadonly();
    protected readonly verduidelijkingstext = computed(() => {
        let tekst = '';
        if (this.#toepassingsTekst()) {
            tekst = this.#toepassingsTekst().replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&') + '</br>';
        }
        return tekst;
    });
    protected readonly labelText = computed(() => {
        return this.#voorwie() ? 'Hoe vlot verliep uw aanvraag  voor ' + this.#voorwie() + '?' : 'Hoe vlot verliep uw aanvraag?';
    });

    #tevredenheidId: any;
    #voorwie = signal<string>(undefined);
    #toepassingsTekst = signal<string>(undefined);

    async ngOnInit(): Promise<void> {
        await this.#create(null);

        this.tevredenheidsForm.controls.reason.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((reasonValue) => {
            // console.log(reasonValue);
            if (reasonValue && reasonValue.length > 0) {
                // console.log(true);
                this.tevredenheidsForm.controls.comment.enable();
                if (this.tevredenheidsForm.controls.comment.value === PLACEHOLDER) {
                    this.tevredenheidsForm.controls.comment.setValue('');
                }
            } else {
                // console.log(false);
                this.tevredenheidsForm.controls.comment.disable();
                this.tevredenheidsForm.controls.comment.setValue(PLACEHOLDER);
            }
        });
    }

    protected outsideClicked() {
        this.#minimize();
    }

    protected async onClickEmotion(emotion): Promise<void> {
        switch (emotion) {
            case 1: {
                this.showWaarom.set(false);
                await this.#submit(1, null);
                this.activeEmotion.set(1);
                this.#minimize();
                break;
            }
            case 2: {
                this.showWaarom.set(false);
                await this.#submit(2, null);
                this.activeEmotion.set(2);
                this.#minimize();
                break;
            }
            case 3: {
                this.showWaarom.set(true);
                await this.#submit(3, null);
                this.activeEmotion.set(3);
                this.minimized.set(false);
                break;
            }
        }
    }
    async onClickSubmit(): Promise<void> {
        await this.#submit(3, this.tevredenheidsForm.controls.comment.value != PLACEHOLDER ? this.tevredenheidsForm.controls.comment.value : '');
        this.#minimize();
    }

    protected openMailOnsDialog(): void {
        this.#dialog.open(StartschermMailOnsDialog, { width: '650px', height: '95vh' });
    }

    #extractData(data): void {
        if (data) {
            this.#voorwie.set(data.voorwienaam);
            if (data.toepassingstekst) {
                this.#toepassingsTekst.set(data.toepassingstekst);
            }
        }
    }

    async #submit(tevredenheid: number, commentaar: string): Promise<void> {
        let callback: () => Promise<void>;
        if (this.tevredenheidsForm.controls.reason.value && this.tevredenheidsForm.controls.reason.value.length > 0) {
            callback = async () => {
                await this.#doPost(tevredenheid, commentaar);
            };
        } else {
            callback = async () => {
                await this.#doPost(tevredenheid, '');
            };
        }

        if (!this.#tevredenheidId) {
            await this.#create(callback);
        } else {
            await callback();
        }
    }

    async #create(callback: () => void): Promise<void> {
        const input: CreateTevredenheidDto = {};
        input.dossiernummer = Number.parseFloat(this.data().dossiernummer);
        input.dossiernummerVersie = this.data().dossierVersie;
        input.url = window.location.pathname;
        input.applicatie = this.#appState.toepassing.code;
        input.aantalVoorwies = this.#startschermStateService.aantalVoorwies;
        input.browser = `${this.#detectBrowserName()} ${this.#detectBrowserVersion()}`;
        this.#extractData(this.data());
        this.#tevredenheidId = await lastValueFrom(this.#staTevredenheidService.createTevredenheid({ body: input }));
        if (callback) callback();
        this.init.set(true);
    }

    async #doPost(tevredenheid: number, commentaar: string): Promise<void> {
        const model: TevredenheidSubmitDto = {
            id: this.#tevredenheidId,
            tevredenheid: tevredenheid,
            commentaar: commentaar,
            tevredenheidsCategorien: this.lvCategories().map((x) => x.id)
        };
        await lastValueFrom(this.#staTevredenheidService.submitTevredenheid({ body: model }));
    }

    #minimize(): void {
        this.minimized.set(true);
    }

    #detectBrowserName(): string {
        const agent = window.navigator.userAgent.toLowerCase();
        switch (true) {
            case agent.indexOf('edge') > -1:
                return 'edge';
            case agent.indexOf('opr') > -1 && !!(window as any).opr:
                return 'opera';
            case agent.indexOf('chrome') > -1 && !!(window as any).chrome:
                return 'chrome';
            case agent.indexOf('trident') > -1:
                return 'ie';
            case agent.indexOf('firefox') > -1:
                return 'firefox';
            case agent.indexOf('safari') > -1:
                return 'safari';
            default:
                return 'other';
        }
    }

    #detectBrowserVersion(): string {
        const userAgent = navigator.userAgent;
        let tem,
            matchTest = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

        if (/trident/i.test(matchTest[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
            return 'IE ' + (tem[1] || '');
        }
        if (matchTest[1] === 'Chrome') {
            tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
            if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
        }
        matchTest = matchTest[2] ? [matchTest[1], matchTest[2]] : [navigator.appName, navigator.appVersion, '-?'];
        if ((tem = userAgent.match(/version\/(\d+)/i)) != null) matchTest.splice(1, 1, tem[1]);
        return matchTest.join(' ');
    }
}
